import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import moment from "moment";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { toast } from "react-toastify";
import { COLOR_PRIMARY } from "../../../constants/theme";
import { useAdvertisementCore } from "../../../cores/advertisements";
import { CampaignStatus } from "../../../cores/advertisements/constants/campaign-status";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useMeCore } from "../../../cores/me";
import { InputWembii } from "../InputWembii";
import ActivateButton from "./ActivateButton";
import { useIntl } from "react-intl";
import CampaignsColumnsPopover, {
  CAMPAIGN_COLUMN_CONFIG,
  CAMPAIGN_COLUMN_LOCKED_INDEX,
  type CampaignColumnKey,
} from "./CampaignsColumnsPopover";
import CampaignsFilterPopover, {
  defaultCampaignFilterDraft,
  type CampaignFilterDraft,
} from "./CampaignsFilterPopover";
import { campaignMatchesToolbarFilters } from "./campaignFilters";
import type { Campaign } from "../../../cores/advertisements/interfaces/api/get-campaigns";
import { formatAdMoney, normalizeAdCurrencyCode } from "./adMoneyFormat";
import "./CampaignsTable.scss";

function formatAdObjective(raw?: string | null): string {
  if (!raw || !String(raw).trim()) return "—";
  return String(raw).replace(/_/g, " ");
}

function formatConversionRateCell(campaign: Campaign): string {
  const { conversionRate, conversions, clicks } = campaign;
  if (conversionRate != null && !Number.isNaN(conversionRate)) {
    const v =
      conversionRate <= 1 && conversionRate >= 0
        ? conversionRate * 100
        : conversionRate;
    return `${Number(v).toFixed(2)}%`;
  }
  if (conversions != null && clicks > 0) {
    return `${((conversions / clicks) * 100).toFixed(2)}%`;
  }
  return "—";
}

/** Misma anchura en cabecera y filas; evita que flex comprima columnas. */
function columnCellStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    flex: "0 0 auto",
  };
}

function RowThumbnail({ imageUrl }: { imageUrl?: string | null }) {
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    setLoadFailed(false);
  }, [imageUrl]);
  const showImg = Boolean(imageUrl?.trim()) && !loadFailed;
  return (
    <div className="fb-campaigns-table__thumb-wrap" aria-hidden>
      {showImg ? (
        <img
          src={imageUrl!}
          alt=""
          className="fb-campaigns-table__thumb-img"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setLoadFailed(true)}
        />
      ) : (
        <div className="fb-campaigns-table__thumb-placeholder">
          <FontAwesomeIcon icon={["fas", "image"]} />
        </div>
      )}
    </div>
  );
}

export type CampaignsTableProps = {
  /** Si es false, solo lista campañas (sin jerarquía conjuntos/anuncios). Usado en la pestaña Campañas. */
  showNestedHierarchy?: boolean;
};

export default function CampaingsTable({
  showNestedHierarchy = true,
}: CampaignsTableProps) {
  const intl = useIntl();
  const [campaignAdset, setCampaignAdset] = useState("");
  const [campaignAd, setCampaignAd] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [appliedFilter, setAppliedFilter] = useState<CampaignFilterDraft>(() =>
    defaultCampaignFilterDraft()
  );
  const [filterDraft, setFilterDraft] = useState<CampaignFilterDraft>(() =>
    defaultCampaignFilterDraft()
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<boolean[]>(() =>
    Array(CAMPAIGN_COLUMN_CONFIG.length).fill(true)
  );

  const filterWrapRef = useRef<HTMLDivElement>(null);
  const columnsWrapRef = useRef<HTMLDivElement>(null);

  const { company } = useMeCore();
  const { settings } = useFacebookAdsCore();
  const currencyCode = normalizeAdCurrencyCode(settings?.currency);
  const {
    campaigns,
    adsets,
    ads,
    minBudget,
    getAdsets,
    getAds,
    getCampaigns,
    putCampaign,
    putAdset,
    putAd,
  } = useAdvertisementCore();

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterOpen && !filterWrapRef.current?.contains(t)) {
        setFilterOpen(false);
      }
      if (columnsOpen && !columnsWrapRef.current?.contains(t)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [filterOpen, columnsOpen]);

  const filteredCampaigns = useMemo(() => {
    let list = campaigns.filter((c) =>
      campaignMatchesToolbarFilters(c, appliedFilter, dateRange)
    );
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => (c.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [campaigns, search, appliedFilter, dateRange]);

  const handleRefresh = useCallback(() => {
    if (!company?.id || !settings?.accountId) return;
    getCampaigns(company.id, settings.accountId);
  }, [company?.id, settings?.accountId, getCampaigns]);

  const applyFilters = useCallback(() => {
    setAppliedFilter(filterDraft);
    setFilterOpen(false);
  }, [filterDraft]);

  const clearFilters = useCallback(() => {
    const d = defaultCampaignFilterDraft();
    setFilterDraft(d);
    setAppliedFilter(d);
    setDateRange(null);
    setFilterOpen(false);
  }, []);

  const toggleColumn = useCallback((idx: number) => {
    if (idx === CAMPAIGN_COLUMN_LOCKED_INDEX) return;
    setColumnVisibility((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      next[CAMPAIGN_COLUMN_LOCKED_INDEX] = true;
      const visible = next.filter(Boolean).length;
      if (visible === 0) return prev;
      return next;
    });
  }, []);

  useEffect(() => {
    setCampaignAdset("");
    setCampaignAd("");
  }, [search]);

  const columnsAdset = useMemo(
    () => [
      { name: "Grupo de anuncios", width: 200 },
      { name: "Estado", width: 200 },
      { name: "Presupuesto Diario", width: 150 },
      { name: "Gasto", width: 150 },
      { name: "Impresiones", width: 150 },
      { name: "Clicks", width: 150 },
      { name: "CPM", width: 150 },
      { name: "CPP", width: 150 },
      { name: "CPC", width: 150 },
      { name: "CTR", width: 150 },
      { name: "Alcance", width: 150 },
    ],
    []
  );

  /** Tabla de anuncios al estilo “lista plana” (referencia competencia). */
  const adsCompetitionColumns = useMemo(
    () =>
      [
        { key: "ad" as const, width: 340 },
        { key: "platform" as const, width: 100 },
        { key: "format" as const, width: 120 },
        { key: "campaign" as const, width: 200 },
        { key: "adset" as const, width: 240 },
        { key: "spend" as const, width: 170 },
      ] as const,
    []
  );

  const handleSelectCampaign = useCallback(
    async (id) => {
      if (id === campaignAdset) {
        setCampaignAdset("");
        setCampaignAd("");
      } else {
        await getAdsets(company?.id!, id);
        setCampaignAdset(id);
      }
    },
    [company, campaignAdset, getAdsets]
  );

  const handleSelectAdset = useCallback(
    async (id) => {
      if (id === campaignAd) {
        setCampaignAd("");
      } else {
        await getAds(company?.id!, id);
        setCampaignAd(id);
      }
    },
    [company, campaignAd, getAds]
  );

  const handleUpdateCampaign = useCallback(
    async (id, status, endDate) => {
      if (!endDate || moment().isBefore(endDate)) {
        if (status === CampaignStatus.ACTIVE.id) {
          putCampaign(company?.id!, id, CampaignStatus.PAUSED.id);
        } else if (status === CampaignStatus.PAUSED.id) {
          putCampaign(company?.id!, id, CampaignStatus.ACTIVE.id);
        }
      }
    },
    [company, putCampaign]
  );

  const handleUpdateAdset = useCallback(
    async (id, status, dailyBudget, changeStatus, endDate) => {
      if (!endDate || moment().isBefore(endDate)) {
        if (changeStatus) {
          if (status === CampaignStatus.ACTIVE.id) {
            putAdset(company?.id!, id, CampaignStatus.PAUSED.id, dailyBudget);
          } else if (status === CampaignStatus.PAUSED.id) {
            putAdset(company?.id!, id, CampaignStatus.ACTIVE.id, dailyBudget);
          }
        } else {
          putAdset(company?.id!, id, status, dailyBudget);
        }
      }
    },
    [company, putAdset]
  );

  const debouncedUpdate = useRef(
    _.debounce((id, status, dailyBudget, endDate) => {
      if (dailyBudget < minBudget?.minDailyBudgetImp!) {
        toast.error(
          `${intl.formatMessage({ id: "app.toast.error64" })} ${minBudget?.minDailyBudgetImp}`
        );
      } else {
        handleUpdateAdset(id, status, dailyBudget, false, endDate);
      }
    }, 1000)
  ).current;

  const handleUpdateAd = useCallback(
    async (id, status, endDate) => {
      if (!endDate || moment().isBefore(endDate)) {
        if (status === CampaignStatus.ACTIVE.id) {
          putAd(company?.id!, id, CampaignStatus.PAUSED.id);
        } else if (status === CampaignStatus.PAUSED.id) {
          putAd(company?.id!, id, CampaignStatus.ACTIVE.id);
        }
      }
    },
    [company, putAd]
  );

  const expandIcon = useCallback(
    (condition) =>
      condition ? (
        <FontAwesomeIcon
          icon={["fas", "angle-down"]}
          size="1x"
          className="mr-1"
          color={COLOR_PRIMARY}
        />
      ) : (
        <FontAwesomeIcon
          icon={["fas", "angle-right"]}
          size="1x"
          className="mr-1"
          color={COLOR_PRIMARY}
        />
      ),
    []
  );

  const renderCampaignRowCell = (
    key: CampaignColumnKey,
    campaign: Campaign
  ): ReactNode => {
    switch (key) {
      case "platform":
        return intl.formatMessage({
          id: "app.advertisements.filters.platformMeta",
        });
      case "format":
        return campaign.objective
          ? campaign.objective.replace(/_/g, " ")
          : "—";
      case "campaign":
        return (
          <>
            {showNestedHierarchy && (
              <span className="fb-campaigns-table__expand">
                {expandIcon(campaignAdset === campaign.id)}
              </span>
            )}
            <RowThumbnail imageUrl={campaign.imageUrl} />
            <div className="fb-campaigns-table__campaign-info">
              <span className="fb-campaigns-table__name-text">
                {campaign.name}
              </span>
              <div className="fb-campaigns-table__campaign-meta">
                {campaign.startTime && (
                  <span>{moment(campaign.startTime).format("L")}</span>
                )}
                {campaign.stopTime && (
                  <>
                    <span className="fb-campaigns-table__meta-sep"> – </span>
                    <span>{moment(campaign.stopTime).format("L")}</span>
                  </>
                )}
                <span
                  className="fb-campaigns-table__campaign-status"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ActivateButton
                    status={campaign.status}
                    endDate={campaign.stopTime}
                    onClick={() =>
                      handleUpdateCampaign(
                        campaign.id,
                        campaign.status,
                        campaign.stopTime
                      )
                    }
                  />
                </span>
              </div>
            </div>
          </>
        );
      case "adsetGroup":
        return "—";
      case "spend":
        return formatAdMoney(campaign.spend, currencyCode);
      case "impressions":
        return campaign.impressions;
      case "clicks":
        return campaign.clicks;
      case "conversions":
        return campaign.conversions ?? "—";
      case "cpm":
        return formatAdMoney(campaign.cpm, currencyCode);
      case "cpc":
        return formatAdMoney(campaign.cpc, currencyCode);
      case "ctr":
        return campaign.ctr;
      case "conversionRate":
        return formatConversionRateCell(campaign);
      default:
        return null;
    }
  };

  return (
    <div className="overflow-auto h-100 fb-campaigns-table">
      <div className="fb-campaigns-table__toolbar">
        <div className="fb-campaigns-table__toolbar-start">
          <div className="fb-campaigns-table__search-wrap">
            <FontAwesomeIcon
              className="fb-campaigns-table__search-icon"
              icon={["fas", "search"]}
            />
            <input
              type="search"
              className="form-control fb-campaigns-table__search"
              placeholder={intl.formatMessage({
                id: "app.advertisements.tableSearchPlaceholder",
              })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={intl.formatMessage({
                id: "app.advertisements.tableSearchPlaceholder",
              })}
            />
          </div>
          <div className="fb-campaigns-table__date-wrap">
            <FontAwesomeIcon
              className="fb-campaigns-table__date-icon"
              icon={["fas", "calendar-alt"]}
            />
            <DateRangePicker
              className="fb-campaigns-table__date-picker"
              onChange={(v) => {
                if (v == null) {
                  setDateRange(null);
                  return;
                }
                if (Array.isArray(v) && v[0] && v[1]) {
                  setDateRange([v[0], v[1]]);
                }
              }}
              // Tipos del picker no reflejan [Date, Date] | null
              value={
                (dateRange ??
                  undefined) as unknown as string | Date | (string | Date)[]
              }
              format="dd/MM/y"
            />
          </div>
        </div>
        <div className="fb-campaigns-table__toolbar-actions">
          <button
            type="button"
            className="fb-campaigns-table__icon-btn"
            onClick={handleRefresh}
            title={intl.formatMessage({
              id: "app.advertisements.toolbar.refresh",
            })}
            aria-label={intl.formatMessage({
              id: "app.advertisements.toolbar.refresh",
            })}
          >
            <FontAwesomeIcon icon={["fas", "sync-alt"]} />
          </button>
          <div className="position-relative" ref={filterWrapRef}>
            <button
              type="button"
              className={cn("fb-campaigns-table__icon-btn", {
                "fb-campaigns-table__icon-btn--active": filterOpen,
              })}
              onClick={() => {
                setColumnsOpen(false);
                if (filterOpen) setFilterOpen(false);
                else {
                  setFilterDraft(appliedFilter);
                  setFilterOpen(true);
                }
              }}
              title={intl.formatMessage({
                id: "app.advertisements.toolbar.filters",
              })}
              aria-expanded={filterOpen}
            >
              <FontAwesomeIcon icon={["fas", "filter"]} />
            </button>
            {filterOpen && (
              <CampaignsFilterPopover
                draft={filterDraft}
                onDraftChange={setFilterDraft}
                onApply={applyFilters}
                onClear={clearFilters}
              />
            )}
          </div>
          <div className="position-relative" ref={columnsWrapRef}>
            <button
              type="button"
              className={cn("fb-campaigns-table__icon-btn", {
                "fb-campaigns-table__icon-btn--active": columnsOpen,
              })}
              onClick={() => {
                setFilterOpen(false);
                setColumnsOpen((o) => !o);
              }}
              title={intl.formatMessage({
                id: "app.advertisements.toolbar.columns",
              })}
              aria-expanded={columnsOpen}
            >
              <FontAwesomeIcon icon={["fas", "columns"]} />
            </button>
            {columnsOpen && (
              <CampaignsColumnsPopover
                visibility={columnVisibility}
                onToggle={toggleColumn}
              />
            )}
          </div>
        </div>
        <span
          className="fb-campaigns-table__currency-pill"
          title={intl.formatMessage(
            { id: "app.advertisements.currencyHint" },
            { currency: currencyCode }
          )}
        >
          {currencyCode}
        </span>
        <span className="fb-campaigns-table__meta">
          {intl.formatMessage(
            { id: "app.advertisements.tableCampaignCount" },
            { count: filteredCampaigns.length }
          )}
        </span>
      </div>

      <div className="fb-campaigns-table__head-row">
        {CAMPAIGN_COLUMN_CONFIG.map((column, idx) =>
          !columnVisibility[idx] ? null : (
            <div
              className="header fb-campaigns-table__col-head"
              key={column.key}
              style={columnCellStyle(column.width)}
            >
              {intl.formatMessage({
                id: `app.advertisements.columns.${column.key}`,
              })}
            </div>
          )
        )}
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="fb-campaigns-table__empty">
          {intl.formatMessage({ id: "app.advertisements.tableNoMatches" })}
        </div>
      ) : (
        <>
          {filteredCampaigns.map((campaign) => (
        <div className="table fb-campaigns-table__campaign" key={campaign.id}>
          <div className="table-row fb-campaigns-table__row">
            {CAMPAIGN_COLUMN_CONFIG.map((col, idx) =>
              !columnVisibility[idx] ? null : (
                <div
                  key={col.key}
                  className={cn(
                    "table-cell fb-campaigns-table__cell",
                    col.key === "campaign" &&
                      showNestedHierarchy &&
                      "pointer justify-content-start fb-campaigns-table__name-cell",
                    col.key === "campaign" &&
                      !showNestedHierarchy &&
                      "justify-content-start fb-campaigns-table__name-cell"
                  )}
                  style={columnCellStyle(col.width)}
                  onClick={
                    col.key === "campaign" && showNestedHierarchy
                      ? () => handleSelectCampaign(campaign.id)
                      : undefined
                  }
                  role={
                    col.key === "campaign" && showNestedHierarchy
                      ? "button"
                      : undefined
                  }
                >
                  {renderCampaignRowCell(col.key, campaign)}
                </div>
              )
            )}
          </div>

          {showNestedHierarchy && campaignAdset === campaign.id && (
            <div className="ml-2 pl-3 border-left border-primary table">
              <div className="fb-campaigns-table__head-row fb-campaigns-table__head-row--nested">
                {columnsAdset.map((column) => (
                  <div
                    className="header fb-campaigns-table__col-head"
                    key={column.name}
                    style={columnCellStyle(column.width)}
                  >
                    {column.name}
                  </div>
                ))}
              </div>
              {adsets.map((adset) => (
                <div key={adset.id}>
                  <div className="table-row fb-campaigns-table__row">
                    {columnsAdset.map((col, idx) => (
                      <div
                        key={col.name}
                        className={cn(
                          "table-cell fb-campaigns-table__cell",
                          idx === 0 &&
                            "pointer justify-content-start fb-campaigns-table__name-cell"
                        )}
                        style={columnCellStyle(col.width)}
                        onClick={
                          idx === 0 ? () => handleSelectAdset(adset.id) : undefined
                        }
                        role={idx === 0 ? "button" : undefined}
                      >
                        {idx === 0 && (
                          <>
                            <span className="fb-campaigns-table__expand">
                              {expandIcon(campaignAd === adset.id)}
                            </span>
                            <RowThumbnail imageUrl={adset.imageUrl} />
                            <span className="fb-campaigns-table__name-text">
                              {adset.name}
                            </span>
                          </>
                        )}
                        {idx === 1 && (
                          <span onClick={(e) => e.stopPropagation()}>
                            <ActivateButton
                              status={adset.status}
                              endDate={campaign.stopTime}
                              onClick={() =>
                                handleUpdateAdset(
                                  adset.id,
                                  adset.status,
                                  adset.dailyBudget,
                                  true,
                                  campaign.stopTime
                                )
                              }
                            />
                          </span>
                        )}
                        {idx === 2 && (
                          <span onClick={(e) => e.stopPropagation()}>
                            <InputWembii
                              type="number"
                              className="w-75 align-items-center"
                              rounded
                              defaultValue={adset.dailyBudget}
                              mb={false}
                              onChange={(v) =>
                                debouncedUpdate(
                                  adset.id,
                                  adset.status,
                                  v,
                                  campaign.stopTime
                                )
                              }
                            />
                          </span>
                        )}
                        {idx === 3
                          ? formatAdMoney(adset.spend, currencyCode)
                          : null}
                        {idx === 4 ? adset.impressions : null}
                        {idx === 5 ? adset.clicks : null}
                        {idx === 6
                          ? formatAdMoney(adset.cpm, currencyCode)
                          : null}
                        {idx === 7
                          ? formatAdMoney(adset.cpp, currencyCode)
                          : null}
                        {idx === 8
                          ? formatAdMoney(adset.cpc, currencyCode)
                          : null}
                        {idx === 9 ? adset.ctr : null}
                        {idx === 10 ? adset.reach : null}
                      </div>
                    ))}
                  </div>

                  {campaignAd === adset.id && (
                    <div className="fb-campaigns-table__ads-nest border-left border-info table">
                      <div className="fb-campaigns-table__head-row fb-campaigns-table__head-row--nested fb-campaigns-table__head-row--ads">
                        {adsCompetitionColumns.map((column) => (
                          <div
                            className="header fb-campaigns-table__col-head"
                            key={`ad-head-${column.key}`}
                            style={columnCellStyle(column.width)}
                          >
                            {intl.formatMessage({
                              id: `app.advertisements.adsCol.${column.key}`,
                            })}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        if (ads.length === 0) {
                          return (
                            <div className="fb-campaigns-table__nested-empty">
                              {intl.formatMessage({
                                id: "app.advertisements.noAdsInAdset",
                              })}
                            </div>
                          );
                        }
                        return ads.map((ad, adIdx) => (
                          <div
                            key={ad.id || `ad-row-${adIdx}`}
                            className="table-row fb-campaigns-table__row fb-campaigns-table__row--ads"
                          >
                            {adsCompetitionColumns.map((col) => (
                              <div
                                key={col.key}
                                className={cn(
                                  "table-cell fb-campaigns-table__cell",
                                  col.key === "ad" &&
                                    "justify-content-start fb-campaigns-table__name-cell fb-campaigns-table__cell--ad-primary"
                                )}
                                style={columnCellStyle(col.width)}
                              >
                                {col.key === "ad" && (
                                  <div className="fb-campaigns-table__ad-primary">
                                    <span
                                      className="fb-campaigns-table__ad-status-wrap"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ActivateButton
                                        status={ad.status}
                                        endDate={campaign.stopTime}
                                        onClick={() =>
                                          handleUpdateAd(
                                            ad.id,
                                            ad.status,
                                            campaign.stopTime
                                          )
                                        }
                                      />
                                    </span>
                                    <RowThumbnail imageUrl={ad.imageUrl} />
                                    <span className="fb-campaigns-table__name-text">
                                      {ad.name}
                                    </span>
                                  </div>
                                )}
                                {col.key === "platform" && (
                                  <span
                                    className="fb-campaigns-table__meta-platform"
                                    title="Meta Ads"
                                  >
                                    <FontAwesomeIcon icon={["fab", "meta"]} />
                                  </span>
                                )}
                                {col.key === "format" &&
                                  formatAdObjective(ad.objective)}
                                {col.key === "campaign" && campaign.name}
                                {col.key === "adset" && (
                                  <div className="fb-campaigns-table__adset-col">
                                    <span className="fb-campaigns-table__adset-name">
                                      {adset.name}
                                    </span>
                                    <span className="fb-campaigns-table__adset-sub">
                                      {intl.formatMessage({
                                        id: "app.advertisements.adsCol.adsetSub",
                                      })}
                                    </span>
                                  </div>
                                )}
                                {col.key === "spend" &&
                                  formatAdMoney(ad.spend, currencyCode)}
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
          ))}
        </>
      )}
    </div>
  );
}
